import { inject, injectable } from 'inversify'
import { makeObservable, observable, runInAction } from 'mobx'
import { HttpGateway } from '@core/services/http.gateway'
import type { ClockGateway } from '@core/services/clock.gateway'
import { Types } from '@core/ioc/types'
import type { UserScopedState } from '@core/interfaces/user-scoped-state'
import { MessagePacking, type ActionFailurePm, type ActionResponsePm } from '@core/feedback/feedback.packing'
import { mapReadFailure } from '@core/services/gateway-failure.helpers'
import { gameSummarySchema, type GameStatus, type TimeControl } from '@shared/index'
import { OperationRepository } from '@core/operations/operation.repository'
import type { OperationHandle, OperationOwner, OperationResult } from '@core/operations/operation.types'
import {
  defineOperation,
  operationFailed,
  operationGroup,
  operationId,
  operationKey,
  operationSucceeded,
} from '@core/operations/operation.helpers'

export type LobbyGamePm = {
  readonly gameId: string
  readonly code: string
  readonly status: GameStatus
  // Whether *this* session created the game or joined it — display only
  // (which name renders under "Host" vs "Opponent"), never sent back to the
  // server, which already knows who is who from the token on each request.
  readonly isHost: boolean
  readonly hostName: string
  // null until the second player has joined.
  readonly guestName: string | null
}

const LOBBY_GROUP = operationGroup('lobby')

// Never retried, same reasoning as the auth writes this mirrors: a create or
// join is not safe to replay blind, and a stuck submit button matters more
// than proving the write never reached the server.
const LOBBY_WRITE_TIMEOUT = {
  kind: 'after' as const,
  milliseconds: 10_000,
  toFailure: () => ({ code: 'temporarily-unavailable' as const }),
}

export const CREATE_GAME_OPERATION = defineOperation<null, ActionFailurePm>({
  id: operationId('lobby.create'),
  key: () => operationKey('create'),
  groups: () => [LOBBY_GROUP],
  pending: { kind: 'ignore' },
  safety: { kind: 'non-idempotent-write' },
  timeout: LOBBY_WRITE_TIMEOUT,
  retry: { kind: 'none' },
  debounce: { kind: 'none' },
})

export const JOIN_GAME_OPERATION = defineOperation<null, ActionFailurePm>({
  id: operationId('lobby.join'),
  key: () => operationKey('join'),
  groups: () => [LOBBY_GROUP],
  pending: { kind: 'ignore' },
  safety: { kind: 'non-idempotent-write' },
  timeout: LOBBY_WRITE_TIMEOUT,
  retry: { kind: 'none' },
  debounce: { kind: 'none' },
})

// A background poll for whether the second seat has been filled — a tick
// firing while the previous one is still in flight is dropped, not joined
// (same reasoning as PRESENCE_CHECK_OPERATION), since the next tick a few
// seconds later makes up for it.
export const REFRESH_GAME_OPERATION = defineOperation<null, ActionFailurePm>({
  id: operationId('lobby.refresh'),
  key: () => operationKey('refresh'),
  groups: () => [LOBBY_GROUP],
  pending: { kind: 'ignore' },
  safety: { kind: 'read' },
  timeout: { kind: 'after', milliseconds: 10_000, toFailure: () => ({ code: 'temporarily-unavailable' }) },
  retry: { kind: 'none' },
  debounce: { kind: 'none' },
})

// A lobby wait is a person staring at the screen, so this is much tighter
// than PresenceRepository's 45s — the host should see the opponent appear
// within a couple of seconds of joining, not half a minute later.
const OPPONENT_POLL_INTERVAL_MS = 3_000

/**
 * Pairing exactly two sessions onto one game before the Duke's own setup
 * phase begins — placing the Duke + Footmen starts only once
 * `gamePm.status === 'ready'`, and lives in `GameRepository`
 * (`features/game/`). This Repository's whole job ends at "both seats are
 * filled".
 */
@injectable()
export class LobbyRepository implements UserScopedState {
  gamePm: LobbyGamePm | null = null

  // The one-shot guard for the auto-navigate into the Duke's setup phase
  // (`LobbyPagePresenter`'s reaction on `gamePm.status === 'ready'`). App-owned
  // from frame one, not loadable — there is no "not yet known" phase, only
  // "not yet entered" — so it starts at its real value, `false`, not `null`
  // (CI-9). Lives here, not on the Transient page Presenter: walking back to
  // `/lobby` after entering the game remounts that Presenter, and a guard
  // that reset with it would bounce the player straight back out.
  hasEnteredGame = false

  private readonly dataGateway: HttpGateway
  private readonly clockGateway: ClockGateway
  private readonly operationOwner: OperationOwner
  private readonly createOperation: OperationHandle<null, ActionFailurePm>
  private readonly joinOperation: OperationHandle<null, ActionFailurePm>
  private readonly refreshOperation: OperationHandle<null, ActionFailurePm>

  // Non-null only while a poll is running — the disposer doubles as the
  // "am I currently watching" flag, same as PresenceRepository's boolean.
  private stopPoll: (() => void) | null = null

  constructor(
    @inject(Types.IDataGateway) dataGateway: HttpGateway,
    @inject(Types.IClockGateway) clockGateway: ClockGateway,
    @inject(OperationRepository) operationRepository: OperationRepository,
  ) {
    this.dataGateway = dataGateway
    this.clockGateway = clockGateway
    this.operationOwner = operationRepository.createOwner({ id: 'lobby.repository', lifetime: 'session' })
    this.createOperation = this.operationOwner.register(CREATE_GAME_OPERATION)
    this.joinOperation = this.operationOwner.register(JOIN_GAME_OPERATION)
    this.refreshOperation = this.operationOwner.register(REFRESH_GAME_OPERATION)

    makeObservable(this, {
      gamePm: observable.ref,
      hasEnteredGame: observable,
    })
  }

  /** Set once by `LobbyPagePresenter`'s auto-navigate reaction, so it fires at most once per game. */
  markEnteredGame = (): void => {
    this.hasEnteredGame = true
  }

  get isCreating(): boolean {
    return this.createOperation.stateFor(null).status === 'loading'
  }

  get isJoining(): boolean {
    return this.joinOperation.stateFor(null).status === 'loading'
  }

  createGame = async (hostName: string, timeControl: TimeControl = null): Promise<ActionResponsePm> => {
    const result = await this.createOperation.run(
      null,
      async ({ signal }) => {
        const response = await this.dataGateway.post(
          '/games',
          { name: hostName, timeControl },
          { signal, idempotencyKey: null },
        )
        if (!response.success) return operationFailed(mapReadFailure(response))

        const parsed = gameSummarySchema.safeParse(response.result)
        if (!parsed.success) return operationFailed({ code: 'invalid-external-data' })

        return operationSucceeded(parsed.data)
      },
      {
        succeeded: (game) => {
          this.gamePm = Object.freeze({
            gameId: game.gameId,
            code: game.code,
            status: game.status,
            isHost: true,
            hostName: game.hostName,
            guestName: game.guestName,
          })
          // The host is the one waiting on someone else's action; a guest
          // already knows both names the moment they join, so only a
          // freshly-created, still-waiting game starts a poll.
          if (game.status === 'waiting-for-opponent') this.startWatching()
        },
        failed: null,
      },
    )

    return this.toActionResponsePm(result)
  }

  joinGame = async (code: string, guestName: string): Promise<ActionResponsePm> => {
    const result = await this.joinOperation.run(
      null,
      async ({ signal }) => {
        const response = await this.dataGateway.post(
          `/games/${encodeURIComponent(code)}/join`,
          { name: guestName },
          { signal, idempotencyKey: null },
        )
        if (!response.success) return operationFailed(mapReadFailure(response))

        const parsed = gameSummarySchema.safeParse(response.result)
        if (!parsed.success) return operationFailed({ code: 'invalid-external-data' })

        return operationSucceeded(parsed.data)
      },
      {
        succeeded: (game) => {
          this.gamePm = Object.freeze({
            gameId: game.gameId,
            code: game.code,
            status: game.status,
            isHost: false,
            hostName: game.hostName,
            guestName: game.guestName,
          })
        },
        failed: null,
      },
    )

    return this.toActionResponsePm(result)
  }

  /**
   * One poll tick: re-fetches the game and, once the second seat is filled,
   * stops the poll. A no-op (`MessagePacking.cancelled`) when there is no
   * game to check, rather than a thrown error — a stray tick can outrace
   * `leave()`/`stopWatching()` by a beat.
   */
  checkForOpponent = async (): Promise<ActionResponsePm> => {
    const current = this.gamePm
    if (current === null) return MessagePacking.cancelled()

    const result = await this.refreshOperation.run(
      null,
      async ({ signal }) => {
        const response = await this.dataGateway.get(`/games/${current.gameId}`, { signal, idempotencyKey: null })
        if (!response.success) return operationFailed(mapReadFailure(response))

        const parsed = gameSummarySchema.safeParse(response.result)
        if (!parsed.success) return operationFailed({ code: 'invalid-external-data' })

        return operationSucceeded(parsed.data)
      },
      {
        succeeded: (game) => {
          // Defined copy: only the fields that can actually change on a
          // poll move, `isHost` stays what it was determined to be at
          // create/join time.
          this.gamePm = Object.freeze({
            gameId: current.gameId,
            code: current.code,
            status: game.status,
            isHost: current.isHost,
            hostName: game.hostName,
            guestName: game.guestName,
          })
          if (game.status === 'ready') this.stopWatching()
        },
        failed: null,
      },
    )

    return this.toActionResponsePm(result)
  }

  /**
   * Registers the poll without an immediate first check — `createGame`'s own
   * response already is that first read. Guarded the same way
   * `PresenceRepository.initialize()` is, so a second call is a no-op.
   */
  private startWatching(): void {
    if (this.stopPoll !== null) return
    this.stopPoll = this.clockGateway.everyInterval(OPPONENT_POLL_INTERVAL_MS, () => {
      void this.checkForOpponent()
    })
  }

  private stopWatching(): void {
    this.stopPoll?.()
    this.stopPoll = null
  }

  /** Leaving the lobby screen before a game exists, or after one ends. */
  leave = (): void => {
    this.stopWatching()
    runInAction(() => {
      this.gamePm = null
      this.hasEnteredGame = false
    })
  }

  resetUserScopedState = (): void => {
    this.stopWatching()
    this.operationOwner.resetAll({ kind: 'session-reset' })
    runInAction(() => {
      this.gamePm = null
      this.hasEnteredGame = false
    })
  }

  private toActionResponsePm(result: OperationResult<unknown, ActionFailurePm>): ActionResponsePm {
    switch (result.kind) {
      case 'succeeded':
        return MessagePacking.succeeded()
      case 'failed':
        return { success: false, failure: result.error }
      case 'cancelled':
      case 'skipped':
        return MessagePacking.cancelled()
    }
  }
}
